'use client'

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, RefreshCcw, Search } from 'lucide-react'

import { Button, InputGroup, InputGroupAddon, InputGroupInput, Skeleton } from '@ui/web/components'
import { ReceiptIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import { formatCurrency, formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { BillingInvoice, BillingType } from './util'
import { useSharedTRPC } from '../../context'
import { InvoiceDetailDialog } from './invoice-detail-dialog'
import { invoiceSearchText, invoiceStatusLabel, invoiceTypeLabel } from './util'

/** How many rows are shown before "Lihat lebih banyak", and how many each click adds. */
const PAGE_SIZE = 5

interface InvoiceHistoryProps {
  type: BillingType
  className?: string
}

/** Riwayat Tagihan: searchable, progressively revealed list of every invoice the org has. */
export function InvoiceHistory({ type, className }: InvoiceHistoryProps) {
  const trpc = useSharedTRPC()

  const [query, setQuery] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  // Search and paging are client-side, so the whole history is fetched once rather than per page.
  const { data, isPending, isError, refetch } = useQuery(
    trpc.myOrganization.getInvoices.queryOptions({ pageSize: 'all' }),
  )

  const invoices = useMemo(() => data?.data ?? [], [data])

  const filteredInvoices = useMemo(() => {
    const keyword = query.trim().toLowerCase()

    if (!keyword) return invoices

    return invoices.filter((invoice) => invoiceSearchText(invoice).includes(keyword))
  }, [invoices, query])

  const visibleInvoices = filteredInvoices.slice(0, visibleCount)
  const hasMore = filteredInvoices.length > visibleInvoices.length

  return (
    <section className={cn('srd:flex srd:w-full srd:flex-col srd:gap-y-4', className)}>
      <header className='srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-3'>
        <h2 className={cn(typographyVariants({ variant: 'title-lg' }), 'srd:text-neutral-black')}>Riwayat Tagihan</h2>

        {/* Nothing to search until at least one invoice exists — an empty history keeps its own copy
            uncluttered. */}
        {!isPending && !isError && invoices.length > 0 && (
          <InputGroup className='srd:w-full srd:max-w-[280px]'>
            <InputGroupAddon align='inline-start'>
              <Search className='srd:size-4 srd:text-blue-100' />
            </InputGroupAddon>

            <InputGroupInput
              placeholder='Cari'
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setVisibleCount(PAGE_SIZE)
              }}
            />
          </InputGroup>
        )}
      </header>

      {(() => {
        if (isError) {
          return (
            <section className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:py-8'>
              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-error')}>
                Gagal memuat data tagihan
              </p>

              <Button
                variant='outline'
                size='sm'
                onClick={async () => {
                  await refetch()
                }}
              >
                <RefreshCcw className='srd:mr-2 srd:size-4' />
                Coba lagi
              </Button>
            </section>
          )
        }

        if (isPending) {
          return (
            <section className='srd:flex srd:w-full srd:flex-col srd:gap-y-2'>
              {new Array(PAGE_SIZE).fill(0).map((_, index) => (
                <Skeleton key={index} className='srd:h-[64px] srd:w-full srd:rounded-xl' />
              ))}
            </section>
          )
        }

        if (invoices.length === 0) {
          return <EmptyHistory type={type} />
        }

        if (filteredInvoices.length === 0) {
          return <NoSearchResult query={query} />
        }

        return (
          <>
            <section className='srd:flex srd:w-full srd:flex-col srd:gap-y-2'>
              {visibleInvoices.map((invoice) => (
                <InvoiceRow key={invoice.id} invoice={invoice} type={type} />
              ))}
            </section>

            {hasMore && (
              <Button
                variant='ghost'
                className='srd:w-fit srd:px-0'
                onClick={() => setVisibleCount((current) => current + PAGE_SIZE)}
              >
                <ChevronDown className='srd:mr-2 srd:size-4' />
                Lihat lebih banyak
              </Button>
            )}
          </>
        )
      })()}
    </section>
  )
}

interface InvoiceRowProps {
  invoice: BillingInvoice
  type: BillingType
}

function InvoiceRow({ invoice, type }: InvoiceRowProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type='button'
        onClick={() => setOpen(true)}
        className='srd:flex srd:w-full srd:cursor-pointer srd:flex-wrap srd:items-center srd:gap-x-4 srd:gap-y-1 srd:rounded-xl srd:bg-grey-10 srd:px-4 srd:py-3 srd:text-left srd:transition-colors srd:hover:bg-grey-20'
      >
        <ReceiptIcon className='srd:size-5' />

        <span className='srd:flex srd:flex-1 srd:flex-wrap srd:items-center srd:gap-x-4 srd:gap-y-1'>
          <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
            {formatDate(invoice.createdAt, { dateStyle: 'medium' })}
          </span>

          <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
            {formatCurrency(invoice.amountPaid)}
          </span>

          <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
            {invoiceTypeLabel(invoice)}
          </span>

          <span
            className={cn(
              typographyVariants({ variant: 'label-sm' }),
              'srd:rounded-sm srd:px-1.5 srd:py-0.5',
              invoice.status === 'PAID' && 'srd:bg-blue-20 srd:text-blue-100',
              invoice.status === 'PENDING' && 'srd:bg-error-container srd:text-error',
              invoice.status === 'EXPIRED' && 'srd:bg-grey-20 srd:text-grey-100',
            )}
          >
            {invoiceStatusLabel(invoice.status)}
          </span>
        </span>
      </button>

      {/* Mounted only once opened so the detail dialog never queries for a row nobody looked at. */}
      {open && <InvoiceDetailDialog invoice={invoice} type={type} open={open} onOpenChange={setOpen} />}
    </>
  )
}

/** No invoices at all — the org has never been billed. */
function EmptyHistory({ type }: { type: BillingType }) {
  return (
    <section className='srd:flex srd:min-h-[30dvh] srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:px-4 srd:text-center'>
      <h3 className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}>Belum ada tagihan</h3>

      <img
        src={getStaticAssetUrl('illustration/red-alien-empty.png')}
        alt=''
        width={150}
        height={150}
        draggable={false}
      />

      <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:max-w-[420px] srd:text-grey-100')}>
        {type === 'school'
          ? 'Riwayat tagihan sekolahmu akan muncul di sini setelah kamu membeli Premium atau menambah kursi.'
          : 'Riwayat tagihanmu akan muncul di sini setelah kamu membeli Premium atau menambah kursi.'}
      </p>
    </section>
  )
}

/** Invoices exist, but none match what was typed. */
function NoSearchResult({ query }: { query: string }) {
  return (
    <section className='srd:flex srd:min-h-[20dvh] srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-2 srd:px-4 srd:text-center'>
      <h3 className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}>
        Tagihan tidak ditemukan
      </h3>

      <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:max-w-[420px] srd:text-grey-100')}>
        Tidak ada tagihan yang cocok dengan &ldquo;{query}&rdquo;.
      </p>
    </section>
  )
}
